import React, { useRef, useState } from 'react';
import logoSrc from '../../assets/images/simpleLogo.jpg';
import { validEmailRegex, validePassword } from '../../utils/validators';
import { Link } from 'react-router-dom';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

export default function LoginForm({ submitData, userAuthError, isLoading, setIsLoading, setUserAuthError, serverError }) {
    const [email, setEmail] = useState('');
    const passwordRef = useRef();

    const [emailError, setEmailError] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [remembered, setRemembered] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsLoading(true);
        const validEamil = validEmailRegex(email);
        const validPassword = validePassword(passwordRef.current.value);

        if (!validEamil) {
            setEmailError(true);
            setIsLoading(false);
            return;
        }

        if (!validPassword) {
            setPasswordError(true);
            setIsLoading(false);
            return;
        }
        setEmailError(false);
        submitData(
            {
                email: email,
                password: passwordRef.current.value,
            },
            remembered
        );
    };

    const handleEmailFocusOut = () => {
        const validEamil = validEmailRegex(email);
        if (!validEamil) {
            setEmailError(true);
        } else {
            setEmailError(false);
        }
    };

    const handlePasswordFocusOut = () => {
        const validPassword = validePassword(passwordRef.current.value);

        if (!validPassword) {
            setPasswordError(true);
        } else {
            setPasswordError(false);
        }
    };

    return (
        <div className='flex flex-1 flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24 mb-36'>
            <div className='mx-auto w-full max-w-sm lg:w-96'>
                <div>
                    <img className='h-20 w-auto' src={logoSrc} alt='Your Company' />
                    <h2 className='mt-6 text-3xl font-bold tracking-tight text-gray-900'>Sign in to your account</h2>
                </div>

                <div className='mt-8'>
                    <div className='mt-6'>
                        <form className='space-y-6'>
                            <div>
                                <label htmlFor='email' className='block text-sm font-medium text-gray-700'>
                                    Email address
                                </label>
                                <div className='mt-1'>
                                    <input
                                        onBlur={handleEmailFocusOut}
                                        onFocus={() => setUserAuthError(false)}
                                        onChange={(e) => setEmail(e.target.value)}
                                        id='email'
                                        name='email'
                                        type='email'
                                        autoComplete='email'
                                        required
                                        className={classNames(
                                            emailError ? 'border-2 border-red-400' : 'border border-gray-300',
                                            'block w-full appearance-none rounded-md  border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-primary focus:outline-none focus:ring-blue-primary sm:text-sm'
                                        )}
                                    />
                                </div>
                                {emailError && <p className='text-red-500 text-xs italic pl-1'>Please use a valid email</p>}
                            </div>

                            <div className='space-y-1'>
                                <label htmlFor='password' className='block text-sm font-medium text-gray-700'>
                                    Password
                                </label>
                                <div className='mt-1'>
                                    <input
                                        ref={passwordRef}
                                        onFocus={() => setUserAuthError(false)}
                                        onBlur={handlePasswordFocusOut}
                                        id='password'
                                        name='password'
                                        type='password'
                                        autoComplete='current-password'
                                        required
                                        className={classNames(
                                            passwordError ? 'border-2 border-red-400' : 'border border-gray-300',
                                            'block w-full appearance-none rounded-md  border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-blue-primary focus:outline-none focus:ring-blue-primary sm:text-sm'
                                        )}
                                    />
                                </div>
                                {passwordError && (
                                    <p className='text-red-500 text-xs italic pl-1'>
                                        Password must be at least 5 characters
                                    </p>
                                )}
                            </div>

                            <div className='flex items-center justify-between'>
                                <div className='flex items-center'>
                                    <input
                                        id='remember-me'
                                        name='remember-me'
                                        type='checkbox'
                                        onChange={(e) => setRemembered(e.target.checked)}
                                        className='h-4 w-4 rounded border-gray-300 text-blue-primary focus:ring-blue-primary'
                                    />
                                    <label htmlFor='remember-me' className='ml-2 mt-2 block text-sm text-gray-900'>
                                        Remember me
                                    </label>
                                </div>

                                <div className='text-sm'>
                                    <Link
                                        to={`/forgot-password/?email=${email}`}
                                        className='font-medium text-blue-secondary hover:text-blue-primary'
                                    >
                                        Forgot your password?
                                    </Link>
                                </div>
                            </div>

                            {!isLoading ? (
                                <div>
                                    <button
                                        onClick={handleSubmit}
                                        type='submit'
                                        className='flex w-full justify-center rounded-md border border-transparent bg-blue-primary py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-blue-primary focus:outline-none focus:ring-2 focus:ring-blue-primary focus:ring-offset-2'
                                    >
                                        <p className='text-white '>Sign in</p>
                                    </button>
                                </div>
                            ) : (
                                <button
                                    disabled
                                    type='button'
                                    className='cursor-pointer w-full text-white bg-blue-primary hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center mr-2 dark:bg-blue-primary dark:hover:bg-blue-primary dark:focus:ring-blue-800 inline-flex items-center justify-center'
                                >
                                    <svg
                                        role='status'
                                        className='inline mr-3 w-4 h-4 text-white animate-spin'
                                        viewBox='0 0 100 101'
                                        fill='none'
                                        xmlns='http://www.w3.org/2000/svg'
                                    >
                                        <path
                                            d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
                                            fill='#E5E7EB'
                                        />
                                        <path
                                            d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
                                            fill='currentColor'
                                        />
                                    </svg>
                                    Loading...
                                </button>
                            )}
                        </form>
                        <p className='text-sm text-center underline py-2 text-blue-primary font-semibold'>
                            <Link to='/sign-up' className='hover:text-gray-500'>
                                Don't have an Account? Sign Up!
                            </Link>
                        </p>
                    </div>
                </div>
                {serverError && (
                    <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative' role='alert'>
                        <p className='font-bold'>There was an error on our end</p>
                        <p className='block sm:inline'>Please retry, if you are unable to login, come back later</p>
                    </div>
                )}
                {userAuthError && (
                    <div className='mt-6'>
                        <div className='rounded-md bg-red-50 p-4'>
                            <div className='flex'>
                                <div className='flex-shrink-0'>
                                    <svg
                                        className='w-6 h-6 text-red-500'
                                        fill='none'
                                        stroke='currentColor'
                                        viewBox='0 0 24 24'
                                        xmlns='http://www.w3.org/2000/svg'
                                    >
                                        <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            strokeWidth='2'
                                            d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                                        ></path>
                                    </svg>
                                </div>
                                <div className='ml-3'>
                                    <h3 className='text-sm font-medium text-red-800'>Authentication Error</h3>
                                    <div className='mt-2 text-sm text-red-700'>
                                        <p>There was an error with your authentication. {userAuthError}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
